import React from 'react';
import { getNameTextColor } from '../../../utils/nameColors';

function formatBookParagraph(text, participants = [], keyOffset = 0) {
  if (!text) return null;

  const quotePattern = /["\u201C]([^\u201D"]+)["\u201D]/g;
  const nameAnnotationPattern = /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g;

  const matches = [];
  let match;

  while ((match = nameAnnotationPattern.exec(text)) !== null) {
    matches.push({
      start: match.index,
      end: match.index + match[0].length,
      type: 'name',
      displayText: match[1],
      lookupName: match[2] || match[1],
    });
  }

  while ((match = quotePattern.exec(text)) !== null) {
    const matchStart = match.index;
    const matchEnd = match.index + match[0].length;
    const matchText = match[0];
    const overlaps = matches.some(
      (m) => matchStart >= m.start && matchStart < m.end
    );
    if (!overlaps) {
      matches.push({
        start: matchStart,
        end: matchEnd,
        type: 'quote',
        text: matchText,
      });
    }
  }

  matches.sort((a, b) => a.start - b.start);

  const parts = [];
  let lastIndex = 0;
  let keyIndex = keyOffset;

  for (const m of matches) {
    if (m.start > lastIndex) {
      parts.push(text.slice(lastIndex, m.start));
    }
    if (m.type === 'quote') {
      parts.push(
        <span key={keyIndex++} className="font-semibold text-gray-900">
          {m.text}
        </span>
      );
    } else {
      const colorClass = getNameTextColor(m.lookupName);
      parts.push(
        <span key={keyIndex++} className={`font-semibold ${colorClass}`}>
          {m.displayText}
        </span>
      );
    }
    lastIndex = m.end;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

const HEADER_COLORS = [
  'bg-[#FEF7ED] border-b-[#FED7AA]',
  'bg-[#FFF1F2] border-b-[#FECDD3]',
  'bg-[#ECFDF5] border-b-[#A7F3D0]',
];

const ChapterPage = React.forwardRef(({ story, pageNumber, pageIndex = 0 }, ref) => {
  if (!story) return <div ref={ref} className="book-page" />;

  const colorIdx = story.chapter_index % HEADER_COLORS.length;
  const paragraphs = (story.narrative || '').split(/\n\n+/).filter((p) => p.trim());

  const isFirstPage = pageIndex === 0;

  return (
    <div ref={ref} className="book-page">
      <div className="absolute inset-0 bg-white overflow-hidden flex flex-col">
        {isFirstPage && (
          <>
            {/* Chapter Header */}
            <div
              className={`relative px-4 sm:px-5 pt-3 pb-2.5 border-b ${HEADER_COLORS[colorIdx]}`}
            >
              {/* Large chapter number watermark */}
              <div
                className="absolute top-0.5 right-2 select-none pointer-events-none"
                style={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: 'clamp(32px, 8vw, 48px)',
                  fontWeight: 700,
                  color: 'rgba(0,0,0,0.04)',
                  lineHeight: 1,
                }}
              >
                {String(story.chapter_index + 1).padStart(2, '0')}
              </div>

              <p
                className="text-[7px] sm:text-[8px] font-bold text-gray-400 uppercase tracking-widest mb-0.5"
                style={{ fontFamily: "'Space Mono', monospace" }}
              >
                Chapter {story.chapter_index + 1}
              </p>

              <h2
                className="text-xs sm:text-sm font-bold text-gray-900 leading-snug pr-6"
                style={{ fontFamily: "'Sora', sans-serif" }}
              >
                {story.title_emoji && (
                  <span className="mr-1">{story.title_emoji}</span>
                )}
                {story.title}
              </h2>

              <p
                className="text-[7px] sm:text-[8px] text-gray-400 uppercase mt-0.5"
                style={{ fontFamily: "'Space Mono', monospace" }}
              >
                {story.date}
              </p>
            </div>

            {/* Tags */}
            {story.tags && story.tags.length > 0 && (
              <div className="flex flex-wrap gap-0.5 px-4 sm:px-5 pt-1.5">
                {story.tags.map((tag, i) => {
                  const tagColors = [
                    'bg-amber-500',
                    'bg-rose-600',
                    'bg-emerald-600',
                    'bg-gray-600',
                  ];
                  return (
                    <span
                      key={i}
                      className={`text-[6px] font-semibold text-white px-1 py-px rounded uppercase tracking-wider ${tagColors[i % tagColors.length]}`}
                    >
                      {tag}
                    </span>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Narrative */}
        <div
          className="flex-1 px-4 sm:px-5 py-2 overflow-hidden"
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 'clamp(8px, 2vw, 10px)',
            lineHeight: 1.65,
            color: '#374151',
          }}
        >
          {paragraphs.map((para, idx) => (
            <p key={idx} className="mb-1.5 text-justify">
              {formatBookParagraph(para.trim(), story.participants, idx * 100)}
            </p>
          ))}
        </div>

        {/* Participants footer */}
        <div className="px-4 sm:px-5 pb-2 mt-auto shrink-0">
          <div className="bg-gray-50 rounded px-2 py-1.5 border border-gray-100">
            <p
              className="text-[6px] font-bold text-rose-600 uppercase tracking-widest mb-px"
              style={{ fontFamily: "'Space Mono', monospace" }}
            >
              Featuring
            </p>
            <p
              className="text-[8px] text-gray-500"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {story.participants?.join(' & ')}
            </p>
          </div>
        </div>

        {pageNumber !== undefined && (
          <div className="book-page-number">{pageNumber}</div>
        )}
      </div>
    </div>
  );
});

ChapterPage.displayName = 'ChapterPage';
export default ChapterPage;
